import { Suspense, lazy } from 'react';

/*
 * The QR scanner, fetched the first time somebody opens it.
 *
 * ────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS AT ALL
 * ────────────────────────────────────────────────────────────────────────
 *
 * html5-qrcode is 360 kB -- the largest single dependency in the bundle, and
 * bigger than react and react-dom together. It was loaded by every visitor on
 * every page load, including the login screen, to support a button that most
 * sessions never press.
 *
 * The scanner itself lives in QRScanner.js, unchanged. This wrapper keeps the
 * name, the path and the props, so all four call sites -- QRCodeField,
 * QRLookupModal, Field and Lab -- did not have to change, and neither did
 * their tests, which mock '../components/QRScanModal' by path.
 *
 * ────────────────────────────────────────────────────────────────────────
 * WHY `open &&` AND NOT JUST <Suspense>
 * ────────────────────────────────────────────────────────────────────────
 *
 * React.lazy fetches the chunk when the lazy element is first RENDERED, not
 * when the module is imported. Rendering <QRScanner open={false}> would
 * therefore fetch 360 kB on every screen that has a scan button, whether or
 * not it is used -- which is the whole cost back again. Returning null while
 * closed means the chunk is requested at the moment the modal opens.
 *
 * ────────────────────────────────────────────────────────────────────────
 * OFFLINE
 * ────────────────────────────────────────────────────────────────────────
 *
 * Lazy does not mean "needs the network". The service worker precaches every
 * /static/ file in asset-manifest.json at install, and vite.config.js's
 * manifest plugin lists every emitted chunk including this one -- so after a
 * first successful load the scanner opens with no signal, which is where a
 * field app needs it most. scripts/verify-build-output.js checks that every
 * chunk index.html and the manifest know about really exists, and
 * src/utils/lazyChunks.test.js checks that the lazy ones are in the manifest
 * rather than trusting that they are.
 *
 * If the chunk genuinely cannot be fetched -- a first visit that went offline
 * mid-session -- the import rejects and the error boundary at the root of the
 * app catches it, which is a message rather than a blank screen.
 */
const QRScanner = lazy(() => import('./QRScanner'));

export default function QRScanModal({ open, onDetected, onClose }) {
  if (!open) return null;
  return (
    // No visible fallback: the chunk is local after the first load and
    // resolves in a frame or two, and a spinner that flashes for 30ms reads
    // as a glitch. The modal's own "starting the camera" state covers the
    // wait that people actually notice.
    <Suspense fallback={null}>
      <QRScanner open={open} onDetected={onDetected} onClose={onClose} />
    </Suspense>
  );
}
